import type { MetaFunction } from '@remix-run/node';

export const meta: MetaFunction = () => {
  return [
    { title: 'Privacy Policy - MatrixFit Recommender' },
    { name: 'description', content: 'Privacy Policy for MatrixFit Recommender by AppScout Labs' },
  ];
};

export default function PrivacyPolicy() {
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1a1a1a', lineHeight: 1.7 }}>
      <h1 style={{ fontSize: '2rem', marginBottom: '8px' }}>Privacy Policy for MatrixFit Recommender</h1>
      <p style={{ color: '#666', marginBottom: '32px' }}>
        <strong>Effective Date:</strong> February 15, 2026 | <strong>Last Updated:</strong> February 15, 2026
      </p>

      <Section title="Introduction">
        <p>
          MatrixFit Recommender ("we," "our," or "the App") is a Shopify application that helps apparel merchants reduce returns by providing size recommendations to shoppers. This Privacy Policy explains how we collect, use, store, and protect your information when you use our App.
        </p>
        <p>
          By installing and using MatrixFit Recommender, you agree to the terms outlined in this Privacy Policy.
        </p>
      </Section>

      <Section title="1. Information We Collect">
        <h3>1.1 Information from Merchants (Store Owners)</h3>
        <p>When you install MatrixFit Recommender, we collect:</p>
        <ul>
          <li><strong>Store Information:</strong> Store name, email address, Shopify domain, and store owner details</li>
          <li><strong>Product Data:</strong> Product titles, descriptions, variants, images, and metadata (including size matrices you upload)</li>
          <li><strong>Usage Data:</strong> How you interact with our App (e.g., matrices created, products assigned, analytics viewed)</li>
          <li><strong>Authentication Data:</strong> OAuth tokens to access your Shopify store (encrypted and stored securely)</li>
        </ul>

        <h3>1.2 Information from Shoppers (End Users)</h3>
        <p>When shoppers use the size finder widget on your store, we collect:</p>
        <ul>
          <li><strong>Measurement Data:</strong> Two body measurements entered by the shopper (e.g., bust and waist measurements in cm or inches)</li>
          <li><strong>Session Information:</strong> Anonymous session ID, browser type, device type, timestamp</li>
          <li><strong>Interaction Data:</strong> Widget opens, form submissions, size recommendations shown, add-to-cart actions</li>
          <li><strong>Product Information:</strong> Which products shoppers are viewing when they use the size finder</li>
        </ul>
        <p><strong>Important:</strong> We do NOT collect personal identifying information (name, email, address), payment information, or IP addresses (beyond what's temporarily logged by our hosting provider).</p>

        <h3>1.3 Automatically Collected Information</h3>
        <ul>
          <li><strong>Analytics Data:</strong> We use aggregated, anonymized analytics to track app performance</li>
          <li><strong>Technical Data:</strong> Browser type, device type, operating system (for compatibility and performance optimization)</li>
        </ul>
      </Section>

      <Section title="2. How We Use Your Information">
        <h3>2.1 Merchant Data</h3>
        <p>We use merchant data to provide and operate the App's functionality, display analytics dashboards, improve our services, provide customer support, send important updates, and comply with legal obligations.</p>

        <h3>2.2 Shopper Data</h3>
        <p>We use shopper data to generate real-time size recommendations, track recommendation accuracy for merchant analytics, improve recommendation algorithms, and aggregate usage statistics (no individual shopper tracking).</p>

        <h3>2.3 Legal Basis for Processing (GDPR)</h3>
        <p>For users in the EEA, UK, or Switzerland: processing is based on contractual necessity, legitimate interests, and consent where required.</p>
      </Section>

      <Section title="3. Data Storage and Retention">
        <ul>
          <li><strong>Merchant Data:</strong> Retained while your store has the App installed, plus 30 days after uninstallation</li>
          <li><strong>Shopper Analytics Data:</strong> Anonymized analytics data is retained for 90 days, then automatically deleted</li>
          <li><strong>Recommendation Events:</strong> Individual shopper measurement data is deleted after 90 days</li>
          <li><strong>Aggregated Statistics:</strong> De-identified, aggregated data may be retained indefinitely for service improvement</li>
        </ul>
        <p>Data is primarily stored in secure databases in data centers located in the United States. Size matrices are stored in your Shopify store's metafields (you retain control).</p>
      </Section>

      <Section title="4. Data Sharing and Disclosure">
        <p><strong>We do NOT sell, rent, or trade any personal information or shopper data to third parties.</strong></p>
        <p>We may share data with trusted service providers (Shopify, hosting providers, analytics services) who are contractually obligated to protect your data. We may disclose information if required by law.</p>
      </Section>

      <Section title="5. Your Rights and Choices">
        <ul>
          <li><strong>Access:</strong> Request a copy of the data we hold about you</li>
          <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
          <li><strong>Update:</strong> Modify your store information and matrices directly in the App</li>
          <li><strong>Delete:</strong> Remove matrices, products, or uninstall the App to delete your data</li>
        </ul>
        <p>Under GDPR, you have additional rights including right to erasure, restriction, objection, withdrawal of consent, and the right to lodge a complaint with your local data protection authority.</p>
        <p>To exercise these rights, contact us at: <strong><a href="mailto:support@labs.appscout.io">support@labs.appscout.io</a></strong></p>
      </Section>

      <Section title="6. Data Security">
        <p>We implement industry-standard security measures: TLS 1.2+ encryption, OAuth 2.0 authentication, role-based access controls, continuous monitoring, and regular security audits.</p>
        <p>In the event of a data breach, we will notify affected users within 72 hours and report to relevant authorities as required by law.</p>
      </Section>

      <Section title="7. Cookies and Tracking">
        <p>We use essential first-party cookies only (session state, user preferences). We do NOT use third-party advertising cookies, cross-site tracking, or social media pixels.</p>
      </Section>

      <Section title="8. Children's Privacy">
        <p>MatrixFit Recommender is not intended for use by individuals under 16. We do not knowingly collect personal information from children.</p>
      </Section>

      <Section title="9. International Data Transfers">
        <p>If located outside the US, your data may be transferred to and processed in the US. We use EU-approved Standard Contractual Clauses and rely on adequacy decisions where applicable.</p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. Major changes will be communicated via email and in-app notice. Minor changes will be reflected in the "Last Updated" date.</p>
      </Section>

      <Section title="11. Contact Us">
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or your data:</p>
        <p>
          <strong>MatrixFit Recommender Support</strong><br />
          Email: <a href="mailto:support@labs.appscout.io">support@labs.appscout.io</a><br />
          Website: <a href="https://matrixfit.labs.appscout.io">https://matrixfit.labs.appscout.io</a><br />
          Company: AppScout Labs<br />
          DPO: <a href="mailto:dpo@labs.appscout.io">dpo@labs.appscout.io</a>
        </p>
      </Section>

      <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        This Privacy Policy is compliant with GDPR, CCPA, Shopify App Store Requirements, PIPEDA, and LGPD.
      </p>
      <p style={{ color: '#666', fontSize: '0.9rem' }}>
        Last Reviewed: February 15, 2026
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '1.4rem', marginBottom: '12px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>{title}</h2>
      {children}
    </section>
  );
}
